import { useContext, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { AppContext, SttDialog } from '../../sporttia/all';
import { useLoader } from '../../lib/hooks';
import {
    useCollectStripePayment,
    useGetStripeReaders,
    useGetStripeTerminal,
    useSimulateReaderPayment,
} from './hooks';
import { getErrorMessage } from '../../lib/utils';
import generalConfig from '../../config/general';

type Status = 'success' | 'loading' | 'error' | 'idle';

type PaymentData = {
    amount: number;
    concept: string;
    idTpv: number;
};

type POSStripePaymentDialogProps = {
    paymentData: PaymentData;
    onClose?: () => void;
    onSuccess?: () => void;
};

/**
 * Component for collecting Stripe payments using the Stripe Terminal SDK.
 */
export default function POSStripePaymentDialog({
    paymentData,
    onClose,
    onSuccess,
}: POSStripePaymentDialogProps) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [connectStripeReaderStatus, setConnectStripeReaderStatus] =
        useState<Status>('idle');
    const { simulateTpvReaderPaymentAsync } = useSimulateReaderPayment();

    const getStripeTerminal = useGetStripeTerminal(cxt.sc?.id, {
        onError: (error: any) => {
            cxt.showMessage('E', getErrorMessage(error));
            onClose?.();
        },
        onDisconnect: () => {
            cxt.showMessage('W', t('stripe.terminal.terminalDisconnect'));
            onClose?.();
        },
    });

    // El SDK de Stripe devuelve sus mensajes en inglés y con detalle técnico,
    // que el centro confunde con errores de Sporttia; el original va a consola.
    const showReaderConnectionError = (error: unknown) => {
        console.error('Error al conectar con el datáfono', error);
        cxt.showMessage('E', t('posPaymentDialog.connectionError'));
        onClose?.();
    };

    const getStripeReaders = useGetStripeReaders(
        getStripeTerminal.terminal,
        cxt.sc?.sporttiaStripeLocationId,
        {
            onSuccess: async (readers) => {
                if (!getStripeTerminal.terminal) {
                    return;
                }

                const readerToConnect = readers[0];

                if (!readerToConnect) {
                    cxt.showMessage('W', t('stripe.terminal.noStripeReaders'));

                    onClose?.();
                    return;
                }

                setConnectStripeReaderStatus('loading');

                const connectResult =
                    await getStripeTerminal.terminal.connectReader(
                        readerToConnect,
                    );

                if ('error' in connectResult) {
                    setConnectStripeReaderStatus('error');
                    showReaderConnectionError(connectResult.error);
                } else {
                    setConnectStripeReaderStatus('success');
                }
            },
            onError: showReaderConnectionError,
        },
    );

    const collectStripePayment = useCollectStripePayment({
        terminal: getStripeTerminal.terminal,
        tpvId: paymentData.idTpv,
        options: {
            enabled: connectStripeReaderStatus === 'success',
            onSuccess,
            onCancel: () => {
                cxt.showMessage('W', t('stripe.terminal.paymentCancelled'));
                onClose?.();
            },
            onError: (error: any) => {
                cxt.showMessage('E', getErrorMessage(error));
                onClose?.();
            },
        },
    });

    const simulatePayment = async () => {
        const { terminal } = getStripeTerminal;

        if (!terminal || !cxt.sc) return;

        await simulateTpvReaderPaymentAsync(
            {
                params: {
                    readerId: terminal.getConnectedReader()?.id,
                    scId: cxt.sc?.id,
                },
            },
            {
                onSuccess,
                onError: (error: any) => {
                    cxt.showMessage('E', getErrorMessage(error));
                    onClose?.();
                },
            },
        );
    };

    const [loading] = useLoader([
        getStripeTerminal.status,
        getStripeReaders.status,
        collectStripePayment.status,
        connectStripeReaderStatus,
    ]);

    return (
        <SttDialog
            open
            title={t('posPaymentDialog.title')}
            maxWidth="sm"
            onClose={onClose}
            content={
                <Box display="flex" flexDirection="column" alignItems="center">
                    {loading && (
                        <>
                            <Typography>{t('loading')}</Typography>
                            <Box mt={2}>
                                <CircularProgress />
                            </Box>
                        </>
                    )}

                    {collectStripePayment.status === 'waiting' && (
                        <>
                            <Typography>
                                {t('stripe.terminal.waitingForPayment')}
                            </Typography>
                            <Box mt={2}>
                                <PaymentIcon color="primary" fontSize="large" />
                            </Box>
                        </>
                    )}

                    {collectStripePayment.status === 'success' && (
                        <>
                            <Typography>
                                {t('stripe.terminal.paymentCompleted')}
                            </Typography>
                            <Box mt={2}>
                                <CheckCircleIcon
                                    style={{ color: '#75bb41' }}
                                    fontSize="large"
                                />
                            </Box>
                        </>
                    )}
                </Box>
            }
            buttons={[
                {
                    type:
                        collectStripePayment.status === 'waiting'
                            ? 'cancel'
                            : 'close',
                    onClick: onClose,
                },
                collectStripePayment.status === 'waiting' &&
                    generalConfig.environment === 'PRE' && {
                        type: 'accept',
                        caption: 'Simulación de pago',
                        onClick: simulatePayment,
                    },
            ]}
        />
    );
}
